import { AdvisoriesNavItem } from '@/components/advisories/AdvisoriesNavItem';
import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shouldBlockProtectedRouteDuringRecovery, isPreviewMfaBypass } from '@/lib/auth';
import { useAalStepUpGate } from '@/hooks/useAalStepUpGate';
import { isSsoSession } from '@/shared/ssoSession';
import { buildAuthRedirectPath } from '@/lib/authReturnTo';
import { isManagedProfileOperator } from '@/lib/supplierProfileStewardship';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { 
  Building2, 
  LayoutDashboard, 
  FileQuestion, 
  Users, 
  Eye, 
  Settings, 
  ChevronDown,
  PanelLeft,
  LogOut,
  ArrowLeftRight,
  FileText,
  Award,
  Shield,
  ShieldAlert,
  ArrowLeft,
  Copy,
} from 'lucide-react';
import { PlatformLogo } from '@/components/PlatformLogo';
import { Button } from '@/components/ui/button';
import { SafeImageFallback } from '@/components/ui/SafeImageFallback';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SidebarMenu, SidebarMenuItem, SidebarProvider, getSidebarOpenFromCookie } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useVendor } from '@/contexts/VendorContext';
import { useMSPAccess } from '@/hooks/useMSPAccess';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { SuperAdminDebugOverlay } from '@/components/debug/SuperAdminDebugOverlay';
import { DebugClipboardButton } from '@/components/debug/DebugClipboardButton';
import { SuperAdminHintsControl } from '@/components/debug/SuperAdminHintsButton';
import { VendorMFAEnforcement } from '@/components/vendor/VendorMFAEnforcement';
import { PendingInvitationBanner } from '@/components/vendor/PendingInvitationBanner';
import { MFAVerification } from '@/components/auth/MFAVerification';
import { SessionManager } from '@/components/auth/SessionManager';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { useToast } from '@/hooks/use-toast';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { AppCommandPalette } from './AppCommandPalette';
import { getSettingsShortcutHint } from '@/lib/settingsShortcut';
import {
  getSupplierAdminNavItem,
  getVisibleSupplierOrgTabs,
  getVisibleSupplierPersonalTabs,
} from '@/lib/adminNavRegistry';
import { GeometricFacetBackground } from './GeometricFacetBackground';
import { SupportNoticeBanner } from './SupportNoticeBanner';
import { ThemeModeMenu } from '@/components/theme/ThemeModeMenu';
import { appBuildInfo, formatBuildInfoForClipboard, formatBuiltAtForDisplay, formatVersionForDisplay } from '@/lib/buildInfo';
import { formatClipboardJson } from '@/lib/clipboardJson';
import { resolveCanonicalLogo } from '@/lib/canonicalLogo';
import {
  CUSTOMER_PROFILE_LABEL,
  ADMIN_PROFILE_LABEL,
  MSP_PROFILE_LABEL,
  SUPPLIER_PROFILE_LABEL,
  SWITCH_PROFILE_LABEL,
} from '@/lib/surfaceViewLabel';
import {
  beginPortalSwitch,
  clearPortalSwitchIntent,
  readPortalSwitchIntent,
  subscribeToPortalSwitchIntent,
} from '@/lib/portalSwitch';
import {
  fetchVendorQuestionnaireVisibility,
  getVendorQuestionnaireVisibilityQueryKey,
} from '@/lib/vendorQuestionnaireVisibility';
import { fromSupportNoticeRow } from '@/lib/supportNotices';
import { normalizeDomainInput } from '@/lib/domainNormalization';
import {
  SurfaceSidebarContent,
  SurfaceSidebarHeader,
  SurfaceSidebarBrandButton,
  SurfaceSidebarCopyButton,
  SurfaceSidebarContextToggleButton,
  SurfaceSidebarNavItemLink,
  SurfaceSidebarPortalLink,
  SurfaceSidebarProfileMenu,
  SurfaceSidebarSection,
  SurfaceSidebarSwitcherControl,
  SurfaceSidebarSwitcherButton,
} from './SurfaceSidebar';
import { FEATURE_FLAG_KEYS } from '@/lib/featureFlagsCatalog';
import {
  fetchSupplierCustomQuestionnaireOpenCount,
  getSupplierCustomQuestionnaireOpenCountQueryKey,
} from '@/lib/customQuestionnaires';

interface VendorLayoutProps {
  children: ReactNode;
}

interface VendorAccessOrganization {
  name?: string | null;
  settings?: {
    mfa_required?: boolean;
  } | null;
}

interface VendorMfaGateCacheEntry {
  hasMFA: boolean;
  mfaRequired: boolean;
  enforcingOrgName: string;
}

const vendorNavItems = [
  { href: '/vendor', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/vendor/profile', label: 'Company Profile', icon: Building2 },
  { href: '/vendor/certifications', label: 'Documentation', icon: Award },
  { href: '/vendor/policies', label: 'Policies', icon: FileText },
  { href: '/vendor/questionnaires', label: 'Questionnaires', icon: FileQuestion },
  { href: '/vendor/actions', label: 'Customer Actions', icon: Shield },
  { href: '/vendor/breach-notifications', label: 'Breach Notices', icon: ShieldAlert, featureKey: FEATURE_FLAG_KEYS.manualBreachNotifications },
  { href: '/vendor/visibility', label: 'Visibility', icon: Eye },
];

// Team is the static base; the Supplier Settings item is derived from the shared registry per
// viewer (ENT-3705 D3) and appended in the render.
const adminNavItems = [
  { href: '/vendor/team', label: 'Team', icon: Users },
];

const vendorSidebarNavClass =
  'w-full overflow-hidden';

const vendorMfaGateCache = new Map<string, VendorMfaGateCacheEntry>();

const getVendorMfaGateCacheKey = (userId?: string | null, vendorProfileId?: string | null) =>
  userId && vendorProfileId ? `${userId}:${vendorProfileId}` : null;

export const clearVendorLayoutMfaGateCache = (cacheKey?: string | null) => {
  if (cacheKey) {
    vendorMfaGateCache.delete(cacheKey);
    return;
  }

  vendorMfaGateCache.clear();
};

export const __resetVendorLayoutMfaGateCache = () => {
  clearVendorLayoutMfaGateCache();
};

/**
 * True inside a subtree already wrapped by a mounted VendorLayout shell.
 *
 * ENT-3079: the Supplier Profile portal now mounts ONE persistent VendorLayout at
 * the route level (see `VendorPortalRoutes` in App.tsx). Pages historically wrapped
 * their own content in `<VendorLayout>`; this context makes any such nested
 * VendorLayout render its children only — never a second shell or a second
 * ENT-2770 step-up gate — so a per-page wrapper can't reintroduce the
 * remount-on-every-menu-click flash.
 */
// Exported for the passthrough unit test (App renders the shell via the route).
export const VendorLayoutPresenceContext = createContext(false);

export function VendorLayout({ children }: VendorLayoutProps) {
  const alreadyInsideVendorShell = useContext(VendorLayoutPresenceContext);
  if (alreadyInsideVendorShell) {
    return <>{children}</>;
  }
  return <VendorLayoutShell>{children}</VendorLayoutShell>;
}

function VendorLayoutShell({ children }: VendorLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const settingsShortcutHint = getSettingsShortcutHint();
  const { user, organization, isLoading: isSessionLoading, returnToMspOrg } = useOrganization();
  const { toast } = useToast();
  const {
    vendorProfile,
    loading,
    userRole,
    contextVendorProfile = null,
    contextClaimableProfiles = [],
    contextDomain = null,
    contextLoading = false,
  } = useVendor();
  const { isViewingAsCustomer, mspOrg, isMSP } = useMSPAccess({ includeCustomers: false });
  const isSuperAdmin = useIsSuperAdmin(user?.id);
  const { enabled: isManualBreachNotificationsEnabled } = useFeatureAccess(FEATURE_FLAG_KEYS.manualBreachNotifications);
  const mfaCacheKey = getVendorMfaGateCacheKey(user?.id, vendorProfile?.id);
  const cachedMfaGate = mfaCacheKey ? vendorMfaGateCache.get(mfaCacheKey) ?? null : null;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profile = useCurrentUserProfile(user?.id);
  const [mfaRequired, setMfaRequired] = useState(cachedMfaGate?.mfaRequired ?? false);
  const [hasMFA, setHasMFA] = useState(cachedMfaGate?.hasMFA ?? true); // Default to true to avoid flash
  const [mfaCheckComplete, setMfaCheckComplete] = useState(cachedMfaGate !== null);
  const [mfaSession, setMfaSession] = useState<{ access_token?: string | null } | null>(null);
  const [mfaSessionReady, setMfaSessionReady] = useState(cachedMfaGate !== null);
  const [enforcingOrgName, setEnforcingOrgName] = useState<string>(cachedMfaGate?.enforcingOrgName ?? '');
  const [isReturningToMsp, setIsReturningToMsp] = useState(false);
  const [portalSwitchIntent, setPortalSwitchIntent] = useState(() => readPortalSwitchIntent());
  const [showSidebarMenu, setShowSidebarMenu] = useState(() => getSidebarOpenFromCookie());
  const activeExitRouteRef = useRef<string | null>(null);
  const isReturningToMspRef = useRef(false);
  const isMountedRef = useRef(true);
  const previousMfaCacheKeyRef = useRef<string | null>(mfaCacheKey);
  const currentRouteKey = `${location.pathname}${location.search}${location.hash}`;
  activeExitRouteRef.current = currentRouteKey;
  const isVendorNoticeRoute = location.pathname.startsWith('/vendor');
  const isVendorClaimRoute = location.pathname.startsWith('/vendor/claim');
  const isGuardedVendorRoute = isVendorNoticeRoute && !isVendorClaimRoute;
  // ENT-6257 AC5: a managed profile has no claimant workspace; its operators open it directly.
  const hasActiveWorkspaceSupplierContext = Boolean(contextVendorProfile)
    || Boolean(contextClaimableProfiles.length)
    || isManagedProfileOperator(vendorProfile, userRole);
  const collapsed = !showSidebarMenu;
  const activeWorkspaceName = vendorProfile?.listing?.company_name || 'Supplier Profile';
  const activeWorkspaceCopyValue = vendorProfile ? `${activeWorkspaceName} (${vendorProfile.id})` : null;
  const activeProfileName = profile?.first_name && profile?.last_name
    ? `${profile.first_name} ${profile.last_name}`
    : user?.email ?? null;
  const activeProfileCopyValue = activeProfileName && user?.id && user?.email
    ? `${activeProfileName} (${user.id}) - ${user.email}`
    : null;
  
  const canManageTeam = userRole === 'owner' || userRole === 'admin';
  const canManageSupplierBilling = canManageTeam;
  // ENT-3705 (D3): Supplier Settings sidebar nav item from the shared registry.
  const supplierAdminNavItem = getSupplierAdminNavItem({ userRole });
  const isMspImpersonating = isViewingAsCustomer && mspOrg;
  const switchProfileLabel = isMSP || isMspImpersonating
    ? MSP_PROFILE_LABEL
    : CUSTOMER_PROFILE_LABEL;
  const commandPaletteItems = useMemo(() => {
    const items = [
      {
        id: 'vendor-dashboard',
        kind: 'page' as const,
        title: 'Dashboard',
        subtitle: 'Overview of supplier activity and profile health',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Dashboard`,
        url: '/vendor',
        icon: LayoutDashboard,
        section: 'Pages' as const,
        keywords: ['home', 'overview', 'supplier dashboard'],
      },
      {
        id: 'vendor-profile',
        kind: 'page' as const,
        title: 'Company Profile',
        subtitle: 'Edit company details and branding',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Company Profile`,
        url: '/vendor/profile',
        icon: Building2,
        section: 'Pages' as const,
        keywords: ['profile', 'company', 'logo'],
      },
      {
        id: 'vendor-certifications',
        kind: 'page' as const,
        title: 'Documentation',
        subtitle: 'Manage certificates and insurance evidence',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Documentation`,
        url: '/vendor/certifications',
        icon: Award,
        section: 'Pages' as const,
        keywords: ['documentation', 'certifications', 'insurance', 'evidence', 'compliance'],
      },
      {
        id: 'vendor-policies',
        kind: 'page' as const,
        title: 'Policies',
        subtitle: 'Review policy documents',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Policies`,
        url: '/vendor/policies',
        icon: FileText,
        section: 'Pages' as const,
        keywords: ['policies', 'documents'],
      },
      {
        id: 'vendor-questionnaires',
        kind: 'page' as const,
        title: 'Questionnaires',
        subtitle: 'Respond to customer questionnaires',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Questionnaires`,
        url: '/vendor/questionnaires',
        icon: FileQuestion,
        section: 'Pages' as const,
        keywords: ['questionnaires', 'forms', 'requests'],
      },
      {
        id: 'vendor-actions',
        kind: 'page' as const,
        title: 'Customer Actions',
        subtitle: 'Track customer remediation tasks',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Customer Actions`,
        url: '/vendor/actions',
        icon: Shield,
        section: 'Pages' as const,
        keywords: ['actions', 'remediation', 'customer actions'],
      },
      ...(isManualBreachNotificationsEnabled
        ? [{
            id: 'vendor-breach-notifications',
            kind: 'page' as const,
            title: 'Breach Notices',
            subtitle: 'Log supplier breach cases and notify linked customers',
            pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Breach Notices`,
            url: '/vendor/breach-notifications',
            icon: ShieldAlert,
            section: 'Pages' as const,
            keywords: ['breach', 'incident', 'notifications', 'customer notices'],
          }]
        : []),
      {
        id: 'vendor-visibility',
        kind: 'page' as const,
        title: 'Visibility',
        subtitle: 'Manage what customers can see',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Visibility`,
        url: '/vendor/visibility',
        icon: Eye,
        section: 'Pages' as const,
        keywords: ['visibility', 'customer view'],
      },
      {
        id: 'vendor-settings',
        kind: 'action' as const,
        title: 'Personal Settings',
        subtitle: 'Profile, security, notifications',
        pathLabel: `Action > ${SUPPLIER_PROFILE_LABEL} > Personal Settings`,
        url: '/vendor/settings',
        icon: Settings,
        section: 'Actions' as const,
        keywords: ['settings', 'preferences', 'profile settings', 'personal settings'],
      },
    ];

    // ENT-3705 (D3): the personal supplier sub-rows derive from the shared registry, so the
    // palette, the sidebar Admin nav item, and the VendorSettings tab strip cannot drift apart
    // (the round-4 missing Supplier-Security palette row was exactly that drift).
    for (const tab of getVisibleSupplierPersonalTabs({ userRole })) {
      items.push({
        id: `vendor-settings-${tab.id}`,
        kind: 'page' as const,
        title: `Personal Settings › ${tab.label}`,
        subtitle: `Supplier portal ${tab.label.toLowerCase()}`,
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Personal Settings > ${tab.label}`,
        url: `/vendor/settings?tab=${tab.id}`,
        icon: Settings,
        section: 'Pages' as const,
        keywords: ['supplier settings', 'personal settings', tab.label.toLowerCase()],
      });
    }

    if (canManageSupplierBilling) {
      // ENT-3705: Supplier Settings (organisation scope) — home row + one registry-derived row per
      // visible org tab (owner-gated Security + Billing), same source the tab strip consumes.
      items.push({
        id: 'vendor-settings-organisation',
        kind: 'page' as const,
        title: 'Supplier Settings',
        subtitle: 'Supplier organisation security and billing',
        pathLabel: `Page > Admin > Supplier Settings`,
        url: '/vendor/settings/organisation',
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['supplier settings', 'organisation settings', 'security', 'billing'],
      });
      for (const tab of getVisibleSupplierOrgTabs({ userRole })) {
        items.push({
          id: `vendor-settings-${tab.id === 'security' ? 'organisation-security' : tab.id}`,
          kind: 'page' as const,
          title: `Supplier Settings › ${tab.label}`,
          subtitle: `Supplier organisation ${tab.label.toLowerCase()}`,
          pathLabel: `Page > Admin > Supplier Settings > ${tab.label}`,
          url: `/vendor/settings/organisation?tab=${tab.id}`,
          icon: Settings,
          section: 'Admin' as const,
          keywords: ['supplier ' + tab.label.toLowerCase(), 'organisation ' + tab.label.toLowerCase(), tab.label.toLowerCase()],
        });
      }
    }

    if (canManageTeam) {
      items.push({
        id: 'vendor-team',
        kind: 'page' as const,
        title: 'Team',
        subtitle: 'Manage supplier portal members',
        pathLabel: `Page > ${SUPPLIER_PROFILE_LABEL} > Team`,
        url: '/vendor/team',
        icon: Users,
        section: 'Pages' as const,
        keywords: ['team', 'members', 'admin'],
      });
    }

    if (organization) {
      items.push({
        id: 'customer-view',
        kind: 'action' as const,
        title: CUSTOMER_PROFILE_LABEL,
        subtitle: `Return to ${organization.name}`,
        pathLabel: `Action > ${SUPPLIER_PROFILE_LABEL} > ${CUSTOMER_PROFILE_LABEL}`,
        url: '/dashboard',
        icon: ArrowLeftRight,
        section: 'Actions' as const,
        keywords: ['customer view', 'switch view', 'back to msp'],
        navigateAfterSelect: true,
        onSelect: async () => {
          beginPortalSwitch('workspace');
          await returnToMspOrg?.();
          navigate('/dashboard');
        },
      });
    }

    return items;
  }, [canManageSupplierBilling, canManageTeam, isManualBreachNotificationsEnabled, navigate, organization, returnToMspOrg]);
  const visibleVendorNavItems = useMemo(
    () => vendorNavItems.filter((item) => {
      if (!('featureKey' in item)) {
        return true;
      }

      if (item.featureKey === FEATURE_FLAG_KEYS.manualBreachNotifications) {
        return isManualBreachNotificationsEnabled;
      }

      return true;
    }),
    [isManualBreachNotificationsEnabled],
  );

  const collapsedNavItemClass = collapsed ? 'mx-auto !size-10 !gap-0 !px-3 !py-2.5 rounded-xl' : '';
  const vendorMainNavContainerClass = collapsed
    ? 'flex-1 min-h-0 overflow-hidden'
    : 'flex-1 min-h-0 overflow-y-auto';
  const vendorBottomSectionClass = collapsed
    ? 'flex-shrink-0 border-t border-emerald-500/20 pt-2'
    : 'flex-shrink-0 border-t border-emerald-500/20';

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    isReturningToMspRef.current = isReturningToMsp;
  }, [isReturningToMsp]);

  useEffect(() => subscribeToPortalSwitchIntent(setPortalSwitchIntent), []);

  useEffect(() => {
    const previousCacheKey = previousMfaCacheKeyRef.current;

    if (previousCacheKey && previousCacheKey !== mfaCacheKey) {
      clearVendorLayoutMfaGateCache(previousCacheKey);
    }

    previousMfaCacheKeyRef.current = mfaCacheKey;
  }, [mfaCacheKey]);

  // D1 (ENT-3463): this vendor-specific enrollment gate must classify the
  // current session, not the identity. An SSO → password swap re-enables the
  // app-MFA gates immediately; an SSO session delegates MFA to the IdP.
  useEffect(() => {
    let active = true;
    let sawAuthEvent = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!active || sawAuthEvent) return;
      setMfaSession(data?.session ?? null);
      setMfaSessionReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      sawAuthEvent = true;
      setMfaSession(session ?? null);
      setMfaSessionReady(true);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setMfaRequired(false);
      setEnforcingOrgName('');
      setMfaCheckComplete(false);
      return;
    }

    if (!vendorProfile?.id) {
      setMfaRequired(false);
      setEnforcingOrgName('');
      setMfaCheckComplete(true);
      return;
    }

    const cachedGate = mfaCacheKey ? vendorMfaGateCache.get(mfaCacheKey) ?? null : null;
    if (cachedGate) {
      setHasMFA(cachedGate.hasMFA);
      setMfaRequired(cachedGate.mfaRequired);
      setEnforcingOrgName(cachedGate.enforcingOrgName);
      setMfaCheckComplete(true);
      return;
    }

    setMfaCheckComplete(false);
  }, [mfaCacheKey, user, vendorProfile?.id]);

  // Check if MFA is required by any connected organization
  useEffect(() => {
    const checkMFARequirement = async () => {
      if (!user || !vendorProfile?.id) {
        setMfaCheckComplete(true);
        return;
      }

      try {
        // Check if user has MFA enabled
        const { data: mfaData } = await supabase.auth.mfa.listFactors();
        const hasVerifiedFactor = mfaData?.totp?.some(f => f.status === 'verified') || false;

        // If user already has MFA, no need to check org requirements
        if (hasVerifiedFactor) {
          if (mfaCacheKey) {
            vendorMfaGateCache.set(mfaCacheKey, {
              hasMFA: true,
              mfaRequired: false,
              enforcingOrgName: '',
            });
          }
          setHasMFA(true);
          setMfaRequired(false);
          setEnforcingOrgName('');
          setMfaCheckComplete(true);
          return;
        }

        // Check if any connected organization requires MFA
        const { data: accessData, error: accessError } = await supabase
          .from('supplier_questionnaire_share_grants')
          .select(`
            organization_id,
            organization:organizations(id, name, settings)
          `)
          .eq('vendor_profile_id', vendorProfile.id)
          .eq('status', 'active');

        if (accessError) {
          console.error('Error checking org access:', accessError);
          setMfaCheckComplete(true);
          return;
        }

        // Check if any organization requires MFA
        const orgRequiringMFA = accessData?.find(access => {
          const organization = access.organization as VendorAccessOrganization | null;
          const settings = organization?.settings;
          return settings?.mfa_required === true;
        });

        if (orgRequiringMFA) {
          const organization = orgRequiringMFA.organization as VendorAccessOrganization | null;
          const nextGateState = {
            hasMFA: false,
            mfaRequired: true,
            enforcingOrgName: organization?.name || 'Connected Organisation',
          };
          if (mfaCacheKey) {
            vendorMfaGateCache.set(mfaCacheKey, nextGateState);
          }
          setHasMFA(false);
          setMfaRequired(true);
          setEnforcingOrgName(nextGateState.enforcingOrgName);
        } else {
          if (mfaCacheKey) {
            vendorMfaGateCache.set(mfaCacheKey, {
              hasMFA: false,
              mfaRequired: false,
              enforcingOrgName: '',
            });
          }
          setHasMFA(false);
          setMfaRequired(false);
          setEnforcingOrgName('');
        }
      } catch (error) {
        console.error('Error checking MFA requirement:', error);
      } finally {
        setMfaCheckComplete(true);
      }
    };

    checkMFARequirement();
  }, [mfaCacheKey, user, vendorProfile?.id]);

  useEffect(() => {
    if (!isSessionLoading && !loading && !user) {
      // ENT-1958: preserve the deep-link destination through sign-in + MFA.
      navigate(buildAuthRedirectPath(location));
    }
    // Save view preference when in vendor mode
    if (user && vendorProfile) {
      localStorage.setItem('currentViewMode', 'vendor');
    }
  }, [user, isSessionLoading, loading, navigate, vendorProfile, location]);

  const handleSignOut = async () => {
    // Use centralized auth utility for consistent global session revocation
    const { signOutGlobally } = await import('@/lib/auth');
    clearVendorLayoutMfaGateCache(mfaCacheKey);
    await signOutGlobally('/vendor/claim/auth', true);
  };

  // ENT-2770: an enrolled vendor whose *session* is still aal1 must complete a
  // step-up challenge before reaching the vendor shell. The assurance probe is
  // deliberately kept OUTSIDE the vendor MFA gate cache (which stores only the
  // enrollment/org-policy verdict) — session AAL is per-session state, never
  // cacheable. Enable signal is `!isPreviewMfaBypass()` (the ENT-1418
  // local/preview bypass, matching sign-in's own carve-out). NOT gated on
  // `hasMFA` (Codex ENT-2770 P2): a transient `listFactors` failure leaves
  // `hasMFA` false, which would silently disable the gate and let an enrolled
  // vendor on an aal1 session reach the shell — bypassing the hook's
  // fail-closed handling. The hook runs its own coalesced `checkMFAStatus`
  // probe to determine enrollment + assurance, and passes vendors with no
  // factor through as `satisfied`.
  const vendorStepUpEnabled =
    mfaCheckComplete && mfaSessionReady && !!vendorProfile && !isPreviewMfaBypass() && !isSsoSession(mfaSession);
  const stepUpGate = useAalStepUpGate(vendorStepUpEnabled);

  const handleCopyVersion = async () => {
    try {
      await navigator.clipboard.writeText(formatBuildInfoForClipboard());
    } catch (error) {
      console.error('Could not copy vendor build info', error);
    }
  };

  const handleCopyActiveWorkspace = async () => {
    if (!activeWorkspaceCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'workspace',
          name: activeWorkspaceName,
          id: vendorProfile?.id ?? null,
          value: activeWorkspaceCopyValue,
        }),
      );
      toast({ title: 'Workspace JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyActiveProfile = async () => {
    if (!activeProfileCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'profile',
          name: activeProfileName,
          id: user?.id ?? null,
          email: user?.email ?? null,
          value: activeProfileCopyValue,
        }),
      );
      toast({ title: 'Profile JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  };

  const vendorLogo = resolveCanonicalLogo({
    manualLogoUrl: vendorProfile?.listing?.logo_url,
    domain: vendorProfile?.listing?.domain ?? vendorProfile?.domain,
  });
  const vendorLogoSrc = vendorLogo.displayUrl;
  const vendorNoticeOrganizationQuery = useQuery({
    queryKey: ['vendor-organization', vendorProfile?.domain ?? 'none'],
    enabled: Boolean(vendorProfile?.domain),
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, slug, settings')
        .order('name', { ascending: true });

      if (error) {
        throw error;
      }

      const vendorDomain = normalizeDomainInput(vendorProfile?.domain);
      if (!vendorDomain) {
        return null;
      }

      const matchingOrganization = (data ?? []).find((candidate) => {
        const settings = candidate.settings as { domain?: string } | null;
        const candidateDomain = normalizeDomainInput(settings?.domain) || normalizeDomainInput(candidate.slug) || null;
        return candidateDomain === vendorDomain;
      });

      return matchingOrganization?.id ?? null;
    },
  });
  const supportNoticeOrganizationId = vendorProfile?.domain
    ? (vendorNoticeOrganizationQuery.data ?? null)
    : (organization?.id ?? mspOrg?.id ?? null);
  const vendorActiveSupportNoticesQuery = useQuery({
    queryKey: ['support-notices', 'active', 'vendor', supportNoticeOrganizationId ?? 'none'],
    enabled: Boolean(supportNoticeOrganizationId),
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_active_support_notices', {
        p_org_id: supportNoticeOrganizationId,
        p_portal: 'vendor',
      });

      if (error) {
        throw error;
      }

      return (data ?? []).map(fromSupportNoticeRow);
    },
    staleTime: 30_000,
  });
  const vendorVisibilityQuery = useQuery({
    queryKey: getVendorQuestionnaireVisibilityQueryKey(vendorProfile?.id),
    enabled: Boolean(vendorProfile?.id),
    staleTime: 30_000,
    queryFn: async () => {
      if (!vendorProfile?.id) {
        return 0;
      }

      const visibility = await fetchVendorQuestionnaireVisibility(vendorProfile.id);
      return visibility.pendingRequests.length;
    },
  });
  const vendorQuestionnaireOpenCountQuery = useQuery({
    queryKey: getSupplierCustomQuestionnaireOpenCountQueryKey(vendorProfile?.id),
    enabled: Boolean(vendorProfile?.id),
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      if (!vendorProfile?.id) {
        return 0;
      }

      return fetchSupplierCustomQuestionnaireOpenCount(vendorProfile.id);
    },
  });
  const shouldHideSupportNotices = portalSwitchIntent !== null;
  const openQuestionnaireRequestCount = vendorQuestionnaireOpenCountQuery.data ?? 0;
  const questionnaireBadgeLabel = openQuestionnaireRequestCount > 99
    ? '99+'
    : String(openQuestionnaireRequestCount);
  const questionnaireNavAriaLabel = openQuestionnaireRequestCount > 0
    ? `Questionnaires, ${openQuestionnaireRequestCount} open customer questionnaire request${openQuestionnaireRequestCount === 1 ? '' : 's'}`
    : 'Questionnaires';
  const pendingVisibilityRequestCount = vendorVisibilityQuery.data ?? 0;
  const visibilityBadgeLabel = pendingVisibilityRequestCount > 9
    ? '9+'
    : String(pendingVisibilityRequestCount);
  const visibilityNavAriaLabel = pendingVisibilityRequestCount > 0
    ? `Visibility, ${pendingVisibilityRequestCount} pending access request${pendingVisibilityRequestCount === 1 ? '' : 's'}`
    : 'Visibility';
  const visibilityNavHref = pendingVisibilityRequestCount > 0
    ? '/vendor/visibility?tab=requests'
    : '/vendor/visibility';

  useEffect(() => {
    if (portalSwitchIntent?.target !== 'vendor') {
      return;
    }

    if (vendorActiveSupportNoticesQuery.isLoading) {
      return;
    }

    clearPortalSwitchIntent('vendor');
  }, [portalSwitchIntent?.target, vendorActiveSupportNoticesQuery.isLoading, supportNoticeOrganizationId]);

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const NavContent = () => (
    <div className="relative z-10 flex h-full min-h-0 flex-col overflow-hidden">
      {/* Supplier View Branding */}
      <SurfaceSidebarHeader
        tone="vendor"
        commandPalette={(
          <AppCommandPalette
            collapsed={collapsed}
            items={commandPaletteItems}
            isMSP={false}
            tone="vendor"
            settingsShortcutPath="/vendor/settings"
          />
        )}
      >
        <div
          data-testid="vendor-branding-row"
          className={cn('flex items-center gap-3', collapsed && 'justify-center')}
        >
          {!collapsed && (
            <SurfaceSidebarBrandButton
              tone="vendor"
              ariaLabel="Open supplier logo settings"
              onClick={() => navigate('/vendor/profile')}
            >
              <SafeImageFallback
                src={vendorLogoSrc}
                alt={vendorProfile?.listing?.company_name || 'Supplier logo'}
                className="h-10 w-10 rounded-lg border bg-white object-cover"
                fallbackSize="md"
                fallbackClassName="text-emerald-100"
                containerClassName="h-10 w-10 rounded-lg bg-emerald-400/20 transition-all group-hover:bg-emerald-400/25 group-hover:shadow-sm"
              />
            </SurfaceSidebarBrandButton>
          )}
          <DropdownMenu>
            <SurfaceSidebarSwitcherControl
              collapsed={collapsed}
            >
              <DropdownMenuTrigger asChild>
                <SurfaceSidebarSwitcherButton
                  tone="vendor"
                  collapsed={collapsed}
                  ariaLabel="Open supplier portal switch menu"
                  title="Supplier Profile"
                  subtitle={vendorProfile?.listing?.company_name || 'Loading...'}
                  trailingAccessory={!collapsed && activeWorkspaceCopyValue ? (
                    <SurfaceSidebarCopyButton
                      tone="vendor"
                      ariaLabel="Copy active workspace email"
                      onClick={() => void handleCopyActiveWorkspace()}
                      className="shrink-0"
                    />
                  ) : undefined}
                />
              </DropdownMenuTrigger>
            </SurfaceSidebarSwitcherControl>
            <DropdownMenuContent align="start" className="z-50 w-56 bg-popover">
              <DropdownMenuItem
                onClick={() => {
                  beginPortalSwitch('vendor');
                  navigate('/vendor');
                }}
                className="text-emerald-600 dark:text-emerald-400"
              >
                <PlatformLogo variant="green" className="mr-2 h-4 w-4" />
                Supplier Profile
              </DropdownMenuItem>
              {organization && (
                <DropdownMenuItem
                  onClick={() => {
                    beginPortalSwitch('workspace');
                    navigate('/dashboard');
                  }}
                  className="text-purple-600 dark:text-purple-400"
                >
                  <PlatformLogo variant="purple" className="mr-2 h-4 w-4" />
                  {CUSTOMER_PROFILE_LABEL}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SurfaceSidebarHeader>

      {/* Main Navigation - Scrollable */}
      <SurfaceSidebarContent>
      <div data-testid="vendor-sidebar-main-nav" className={vendorMainNavContainerClass}>
          <SurfaceSidebarSection tone="vendor">
              <SidebarMenu className={cn(collapsed && 'items-center')}>
                {visibleVendorNavItems.map((item) => {
                  const isQuestionnairesItem = item.href === '/vendor/questionnaires';
                  const isVisibilityItem = item.href === '/vendor/visibility';
                  const showQuestionnaireBadge = isQuestionnairesItem && openQuestionnaireRequestCount > 0;
                  const showVisibilityBadge = isVisibilityItem && pendingVisibilityRequestCount > 0;
                  const navHref = isVisibilityItem ? visibilityNavHref : item.href;

                  return (
                    <SurfaceSidebarNavItemLink
                      key={item.href}
                      tone="vendor"
                      to={navHref}
                      title={item.label}
                      icon={item.icon}
                      collapsed={collapsed}
                      ariaLabel={isQuestionnairesItem ? questionnaireNavAriaLabel : isVisibilityItem ? visibilityNavAriaLabel : undefined}
                      trailingAccessory={showQuestionnaireBadge ? (
                        <Badge
                          data-testid="vendor-questionnaire-open-count"
                          aria-hidden="true"
                          className="h-5 min-w-5 justify-center rounded-full border border-amber-100/80 bg-amber-300 px-1.5 py-0 text-[10px] font-semibold leading-none tabular-nums text-amber-950 hover:bg-amber-300"
                        >
                          {questionnaireBadgeLabel}
                        </Badge>
                      ) : showVisibilityBadge ? (
                        <Badge className="h-5 min-w-5 justify-center rounded-full border-0 bg-white/20 px-1.5 py-0 text-[10px] font-semibold leading-none tabular-nums text-white hover:bg-white/20">
                          {visibilityBadgeLabel}
                        </Badge>
                      ) : undefined}
                      collapsedAccessory={showQuestionnaireBadge ? (
                        <span
                          data-testid="vendor-questionnaire-open-count-collapsed"
                          aria-hidden="true"
                          className="flex h-4 min-w-4 items-center justify-center rounded-full border border-amber-100 bg-amber-300 px-1 text-[8px] font-bold leading-none tabular-nums text-amber-950 shadow-[0_0_0_2px_rgba(6,78,59,0.92)]"
                        >
                          {questionnaireBadgeLabel}
                        </span>
                      ) : showVisibilityBadge ? (
                        <span
                          aria-hidden="true"
                          className="block h-2.5 w-2.5 rounded-full bg-emerald-200 shadow-[0_0_0_2px_rgba(6,78,59,0.92)]"
                        />
                      ) : undefined}
                      collapsedNavItemClass={cn(vendorSidebarNavClass, collapsedNavItemClass)}
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  );
                })}
                {/* ENT-5889 (v1.12): exposure resolved server-side from the caller's claimed listings. */}
                <AdvisoriesNavItem
                  audience="vendor"
                  tone="vendor"
                  collapsed={collapsed}
                  collapsedNavItemClass={cn(vendorSidebarNavClass, collapsedNavItemClass)}
                  onClick={() => setMobileMenuOpen(false)}
                />
              </SidebarMenu>
          </SurfaceSidebarSection>

          {canManageTeam && (
            <SurfaceSidebarSection tone="vendor" label="Admin" className="mt-0.5">
                <SidebarMenu className={cn(collapsed && 'items-center')}>
                  {adminNavItems.map((item) => {
                    return (
                      <SurfaceSidebarNavItemLink
                        key={item.href}
                        tone="vendor"
                        to={item.href}
                        title={item.label}
                        icon={item.icon}
                        collapsed={collapsed}
                        collapsedNavItemClass={cn(vendorSidebarNavClass, collapsedNavItemClass)}
                        onClick={() => setMobileMenuOpen(false)}
                      />
                    );
                  })}
                  {/* ENT-3705 (D3): Supplier Settings nav item from the shared registry. */}
                  {supplierAdminNavItem && (
                    <SurfaceSidebarNavItemLink
                      key={supplierAdminNavItem.url}
                      tone="vendor"
                      to={supplierAdminNavItem.url}
                      title={supplierAdminNavItem.title}
                      icon={supplierAdminNavItem.icon}
                      collapsed={collapsed}
                      collapsedNavItemClass={cn(vendorSidebarNavClass, collapsedNavItemClass)}
                      onClick={() => setMobileMenuOpen(false)}
                    />
                  )}

                  {userRole === 'owner' && !organization && (
                    <SidebarMenuItem>
                      <Link
                        to="/onboarding"
                        aria-label="Manage Your Supply Chain"
                        data-sidebar="menu-button"
                        data-size="default"
                        data-active={false}
                        data-state="closed"
                        state={{
                          prefillOrgName: vendorProfile?.listing?.company_name,
                          fromVendorProfileId: vendorProfile?.id,
                        }}
                        onClick={() => setMobileMenuOpen(false)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                          vendorSidebarNavClass,
                          collapsedNavItemClass,
                          'text-purple-200 hover:bg-purple-500/20 hover:text-purple-100',
                        )}
                      >
                        <Shield className="h-4 w-4 shrink-0" />
                        {!collapsed && <span>Manage Your Supply Chain</span>}
                      </Link>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
            </SurfaceSidebarSection>
          )}
      </div>

      {/* Bottom Fixed Section */}
      <div className={vendorBottomSectionClass}>
        {organization && (
          <SurfaceSidebarSection tone="vendor" label={SWITCH_PROFILE_LABEL} className="py-2">
              <SidebarMenu>
                <SurfaceSidebarPortalLink
                  tone="vendor"
                  to="/dashboard"
                  title={switchProfileLabel}
                  icon={ArrowLeftRight}
                  collapsed={collapsed}
                  collapsedNavItemClass={collapsed ? 'mx-auto !size-10 !gap-0 !px-3 !py-2.5 rounded-xl' : ''}
                  onClick={() => beginPortalSwitch('workspace')}
                  accent="purple"
                />
                {isSuperAdmin ? (
                  <SurfaceSidebarPortalLink
                    tone="vendor"
                    to="/admin"
                    title={ADMIN_PROFILE_LABEL}
                    icon={Settings}
                    collapsed={collapsed}
                    collapsedNavItemClass={collapsed ? 'mx-auto !size-10 !gap-0 !px-3 !py-2.5 rounded-xl' : ''}
                    onClick={() => beginPortalSwitch('admin')}
                    accent="rose"
                  />
                ) : null}
              </SidebarMenu>
          </SurfaceSidebarSection>
        )}

          <div className="space-y-2 p-4">
            <SurfaceSidebarProfileMenu
              tone="vendor"
              collapsed={collapsed}
              avatarSrc={profile?.avatar_url || undefined}
              initials={getInitials()}
              name={profile?.first_name && profile?.last_name
                ? `${profile.first_name} ${profile.last_name}`
                : user?.email}
              roleLabel={userRole || 'Member'}
              onAvatarClick={() => navigate('/vendor/settings')}
              avatarAriaLabel="Open profile icon"
              profileTriggerAriaLabel="Open profile menu"
              profileActionsTestId="vendor-profile-actions"
              copyButton={!collapsed && activeProfileCopyValue ? {
                ariaLabel: 'Copy active profile email',
                onClick: () => void handleCopyActiveProfile(),
              } : undefined}
            >
              <ThemeModeMenu />
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/vendor/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Personal Settings
                <DropdownMenuShortcut>{settingsShortcutHint}</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyVersion}>
                <Copy className="h-4 w-4 mr-2" />
                <div className="flex min-w-0 flex-col">
                  <span className="whitespace-nowrap text-[11px] leading-none">{formatVersionForDisplay(appBuildInfo.version)}</span>
                  <span className="whitespace-nowrap text-[10px] leading-tight text-muted-foreground">
                    Built {formatBuiltAtForDisplay(appBuildInfo.builtAtUtc)}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </SurfaceSidebarProfileMenu>
          </div>
        </div>
      </SurfaceSidebarContent>
    </div>
  );

  const handleMFAEnabled = () => {
    if (mfaCacheKey) {
      vendorMfaGateCache.set(mfaCacheKey, {
        hasMFA: true,
        mfaRequired: false,
        enforcingOrgName: '',
      });
    }
    setHasMFA(true);
    setMfaRequired(false);
  };

  if (isSessionLoading || loading || contextLoading || !mfaCheckComplete || !mfaSessionReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-300">Loading...</div>
      </div>
    );
  }

  if (isReturningToMsp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-300">Returning to MSP workspace...</div>
      </div>
    );
  }

  if (
    isGuardedVendorRoute &&
    organization &&
    !contextVendorProfile &&
    contextClaimableProfiles.length > 0
  ) {
    const claimDomain = normalizeDomainInput(contextDomain);
    return (
      <Navigate
        to={`/vendor/claim${claimDomain ? `?domain=${encodeURIComponent(claimDomain)}` : ''}`}
        replace
      />
    );
  }

  if (isGuardedVendorRoute && organization && !hasActiveWorkspaceSupplierContext) {
    const workspaceName = organization.name || 'This workspace';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6 dark:from-slate-950 dark:to-slate-900">
        <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center">
          <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-50">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Supplier profile unavailable for this workspace</AlertTitle>
            <AlertDescription className="space-y-4">
              <p>
                {workspaceName} does not have a Supplier Profile or claimable supplier path.
                Switch to a supplier workspace before opening Supplier Profile pages.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Return to current workspace
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // SECURITY: Hard enforcement - if in recovery mode, block access to protected routes
  // User must complete password reset before accessing any app functionality
  // Note: We don't redirect here because it causes a loop - Index.tsx handles showing the reset form
  if (shouldBlockProtectedRouteDuringRecovery(location.pathname)) {
    console.log('[Security] Recovery mode active in VendorLayout - blocking vendor access');
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-8">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold mb-2">Password Reset Required</h2>
          <p className="text-muted-foreground mb-4">
            You must complete the password reset process before accessing the platform.
          </p>
          {/* ENT-4311: point at the resumable /auth/reset route — it reconstructs the
              in-progress recovery (update-password/MFA) from the live session, whereas `/`
              only stops routing and would strand the reset. /auth/reset is a recovery path
              (isRecoveryPath), so it is not blocked while recovery mode is active. */}
          <a href="/auth/reset" className="text-primary hover:underline">
            Return to password reset
          </a>
        </div>
      </div>
    );
  }

  // Show MFA enforcement screen if required and user doesn't have MFA
  if (mfaRequired && !hasMFA && vendorProfile && !isSsoSession(mfaSession)) {
    return (
      <VendorMFAEnforcement
        vendorName={vendorProfile.listing?.company_name || vendorProfile.domain}
        organizationName={enforcingOrgName}
        onMFAEnabled={handleMFAEnabled}
      />
    );
  }

  // ENT-2770: enrolled vendor but the session is aal1 — challenge before shell.
  if (stepUpGate.status === 'step-up-required' && stepUpGate.factorId) {
    return (
      <MFAVerification
        factorId={stepUpGate.factorId}
        onSuccess={stepUpGate.recheck}
        onCancel={handleSignOut}
      />
    );
  }

  if (vendorStepUpEnabled && stepUpGate.status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="text-sm text-muted-foreground">Verifying your two-factor session…</p>
      </div>
    );
  }

  if (stepUpGate.status === 'unavailable') {
    // Fail closed: the assurance probe could not confirm an aal2 session.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <p className="max-w-sm text-sm text-muted-foreground">
          We couldn't verify your two-factor session. Retry, or return to sign in and try again.
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={stepUpGate.recheck}>
            Retry
          </Button>
          <Button type="button" onClick={() => { void handleSignOut(); }}>
            Return to sign in
          </Button>
        </div>
      </div>
    );
  }

  // Session timeout - 15 minutes with 2 minute warning
  const sessionTimeoutMinutes = 15;

  return (
    <VendorLayoutPresenceContext.Provider value={true}>
    <SessionManager timeoutMinutes={sessionTimeoutMinutes} warningMinutes={2}>
      <SidebarProvider defaultOpen={showSidebarMenu} open={showSidebarMenu} onOpenChange={setShowSidebarMenu}>
        <div className="flex h-svh w-full overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
          {/* Desktop Sidebar */}
          <aside
            data-state={collapsed ? 'collapsed' : 'expanded'}
            data-collapsible={collapsed ? 'icon' : ''}
            className={cn(
              "group relative hidden md:flex h-svh flex-col flex-shrink-0 overflow-hidden bg-gradient-to-b from-emerald-900/95 via-emerald-800/95 to-teal-900/95 border-r border-emerald-700/30 transition-[width] duration-200 ease-linear",
              showSidebarMenu ? "w-64" : "w-12",
            )}
          >
            <GeometricFacetBackground
              tone="vendor"
              idPrefix="vendor-sidebar-depth"
              testId="vendor-sidebar-backdrop"
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden [mask-image:linear-gradient(to_bottom,rgba(0,0,0,0.98)_0%,rgba(0,0,0,0.88)_34%,rgba(0,0,0,0.46)_56%,rgba(0,0,0,0.12)_74%,transparent_92%)]"
              glowClassName={showSidebarMenu
                ? 'absolute left-1/2 top-[-18%] h-72 w-[120%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl'
                : 'absolute left-1/2 top-[-14%] h-56 w-[160%] -translate-x-1/2 rounded-[42%] bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.24),_rgba(255,255,255,0)_58%)] opacity-95 blur-3xl'}
              washClassName={showSidebarMenu
                ? 'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.14),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_56%,_rgba(110,231,183,0.16),_rgba(16,185,129,0)_34%)] opacity-95'
                : 'absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(255,255,255,0.14),_rgba(255,255,255,0)_20%),radial-gradient(circle_at_50%_50%,_rgba(110,231,183,0.14),_rgba(16,185,129,0)_28%)] opacity-90'}
              svgClassName={showSidebarMenu
                ? 'absolute top-[-16%] left-1/2 h-[118%] w-[232%] -translate-x-1/2 opacity-[0.86]'
                : 'absolute top-[-12%] left-1/2 h-[112%] w-[156%] -translate-x-1/2 opacity-[0.78]'}
            />
            <NavContent />
          </aside>

          {/* Main Content Area */}
          <div className="platform-view-pane relative flex min-w-0 flex-1 flex-col overflow-hidden">
            <GeometricFacetBackground
              tone="vendor"
              idPrefix="vendor-main-depth"
              testId="vendor-shell-depth-layer"
              className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
              glowClassName="absolute left-1/2 top-[-6%] h-[24rem] w-[40rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.12),_rgba(255,255,255,0)_62%)] opacity-95 blur-3xl"
              washClassName="absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,_rgba(196,181,253,0.14),_rgba(255,255,255,0)_24%),radial-gradient(circle_at_50%_68%,_rgba(34,197,94,0.12),_rgba(255,255,255,0)_26%)] opacity-85"
              svgClassName="absolute top-[-16%] left-1/2 h-[120%] w-[118%] -translate-x-1/2 opacity-[0.18]"
            />
          {isVendorNoticeRoute ? (
            <SupportNoticeBanner
              notices={shouldHideSupportNotices ? [] : vendorActiveSupportNoticesQuery.data}
              isLoading={vendorActiveSupportNoticesQuery.isLoading || shouldHideSupportNotices}
              errorMessage={vendorActiveSupportNoticesQuery.error instanceof Error ? vendorActiveSupportNoticesQuery.error.message : null}
            />
          ) : null}
          {/* MSP Impersonation Banner - Only spans content area on desktop */}
          {isMspImpersonating && (
            <div className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2.5 flex items-center justify-between z-40">
              <div className="flex items-center gap-3 text-sm">
                <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 flex items-center gap-1.5">
                  <Eye className="h-3 w-3" />
                  {SUPPLIER_PROFILE_LABEL}
                </Badge>
                <span className="hidden sm:inline">
                  Viewing as <strong>{vendorProfile?.listing?.company_name}</strong>
                  {mspOrg && <span className="opacity-80"> • Managed by {mspOrg.name}</span>}
                </span>
                <strong className="sm:hidden">{vendorProfile?.listing?.company_name}</strong>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (isReturningToMspRef.current) {
                    return;
                  }

                  const exitRoute = activeExitRouteRef.current;
                  beginPortalSwitch('workspace');
                  setIsReturningToMsp(true);
                  try {
                    if (returnToMspOrg) {
                      await returnToMspOrg();
                    }
                  } catch (error) {
                    console.error('Failed to restore MSP org before exiting supplier view', error);
                  } finally {
                    if (isMountedRef.current && activeExitRouteRef.current === exitRoute) {
                      navigate('/customers', { replace: true });
                    }
                    if (isMountedRef.current) {
                      setIsReturningToMsp(false);
                    }
                  }
                }}
                disabled={isReturningToMsp}
                className="text-white hover:bg-white/20 gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Exit {SUPPLIER_PROFILE_LABEL}</span>
                <span className="sm:hidden">Exit</span>
              </Button>
            </div>
          )}

          {/* Mobile Header */}
          <div className={cn(
            "md:hidden fixed left-0 right-0 z-50 bg-gradient-to-r from-emerald-900 to-teal-900 border-b border-emerald-700/30",
            isMspImpersonating ? "top-10" : "top-0"
          )}>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Open supplier logo settings"
                  onClick={() => navigate('/vendor/profile')}
                  className="group flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-emerald-950/20 transition-all hover:scale-[1.02] hover:bg-emerald-500/15 hover:shadow-sm"
                >
                  <SafeImageFallback
                    src={vendorLogoSrc}
                    alt={vendorProfile?.listing?.company_name || 'Supplier logo'}
                    className="h-full w-full rounded object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    fallbackSize="sm"
                    fallbackClassName="text-emerald-100"
                    containerClassName="flex h-5 w-5 items-center justify-center rounded bg-emerald-400/20"
                  />
                </button>
                <span className="font-bold text-emerald-50">{SUPPLIER_PROFILE_LABEL}</span>
                {isMspImpersonating && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-400/50 text-blue-200">
                    Managed
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <SuperAdminDebugOverlay />
                <NotificationBell variant="vendor" />
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-200" aria-label="Open sidebar menu">
                      <PanelLeft />
                    </Button>
                  </SheetTrigger>
                  <SheetContent 
                    side="left" 
                    className="w-64 p-0 overflow-hidden bg-gradient-to-b from-emerald-900 via-emerald-800 to-teal-900 border-emerald-700/30"
                  >
                    <NavContent />
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>

          {/* Main Content */}
            <main className={cn(
            "relative z-10 min-h-0 flex-1 overflow-y-auto overflow-x-hidden",
            isMspImpersonating ? "md:pt-0 pt-[calc(2.5rem+4rem)]" : "md:pt-0 pt-16"
          )}>
          {/* Top Header with Notification Bell */}
            <header
              data-testid="vendor-shell-header"
              className="sticky top-0 z-30 hidden h-14 shrink-0 items-center justify-between gap-4 border-b border-border/30 bg-white/50 px-4 sm:px-6 backdrop-blur-sm md:flex dark:bg-slate-900/50"
            >
              <div className="flex min-w-0 items-center gap-3">
                <SurfaceSidebarContextToggleButton className="-ml-2 h-7 w-7 rounded-md hover:bg-black/5 dark:hover:bg-white/10" />
              </div>
              <div className="flex items-center gap-2">
                <SuperAdminDebugOverlay />
                <NotificationBell variant="vendor" />
              </div>
            </header>
            <div
              className="p-6 md:p-8"
            >
              <PendingInvitationBanner />
              {children}
            </div>
          </main>
          <DebugClipboardButton companionWidgetIds={[]} />
          <SuperAdminHintsControl />
          </div>
        </div>
      </SidebarProvider>
    </SessionManager>
    </VendorLayoutPresenceContext.Provider>
  );
}
